import React, {Component, ReactNode} from 'react';
import CustomTable, {CustomColumnProps} from '../../custom/CustomTable';
import CustomPaginator, {CustomPaginatorProps} from '../../custom/CustomPaginator';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import CustomLoading from '../../custom/CustomLoading';
import Icon from '../../../icons/Icon';
import {ConfirmPopup} from 'primereact/confirmpopup';
import {customConfirm} from '../../custom/CustomPopupConfirm';
import LanguageUtil from '../../../util/LanguageUtil';
import RoleHelper from '../../../RoleHelper';
import ComponentName from '../../../constant/ComponentName';
import {CustomListModel} from '../../../domain/custom-list/CustomListDomains';
import moment from 'moment';
import {DataTableRowClickEventParams, DataTableSelectionChangeParams} from 'primereact/datatable';

interface Props {
  lists: CustomListModel[];
  selectedLists: CustomListModel[];
  loading: boolean;
  onRowClick: (list: CustomListModel) => void;
  onDelete: (list: CustomListModel) => Promise<void>;
  onSelectionChange: (lists: CustomListModel[]) => void;
}

class State {
  public rows: number = 10;
  public currentPage: number = 1;
}

class CustomListTableComponent extends Component<Props, State> {
  public state: State = new State();

  public render(): ReactNode {
    const {lists, loading} = this.props;
    const {rows, currentPage} = this.state;

    if (loading) {
      return <CustomLoading/>;
    }

    const currentData: CustomListModel[] = this.getCurrentPageLists();
    const pageSelection: CustomListModel[] = this.getCurrentPageSelection(currentData);

    return (
      <div className={'w-full h-full flex flex-col overflow-y-hidden'}>
        <ConfirmPopup/>
        <div className={'overflow-y-scroll h-[calc(100vh-320px)] flex w-full rounded-lg'}>
          <CustomTable
            value={currentData}
            columns={this.getColumns()}
            selectionMode={RoleHelper.canEdit(ComponentName.CUSTOM_LIST) ? 'checkbox' : undefined}
            selection={pageSelection}
            dataKey={'id'}
            onSelectionChange={this.onSelectionChange}
            onRowClick={(e: DataTableRowClickEventParams) => this.props.onRowClick(e.data as CustomListModel)}
          />
        </div>
        <div className={'paginator'}>
          <CustomPaginator
            rows={rows}
            totalRecords={lists.length}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            currentPage={currentPage}
            onPageChange={this.onPageChange}
          />
        </div>
      </div>
    );
  }

  public componentDidUpdate(prevProps: Props): void {
    const {rows, currentPage} = this.state;

    if (prevProps.lists === this.props.lists) {
      return;
    }

    const maxPage: number = Math.max(1, Math.ceil(this.props.lists.length / rows) || 1);
    if (currentPage > maxPage) {
      this.setState({currentPage: maxPage});
    }
  }

  private onPageChange = (event: CustomPaginatorProps): void => {
    this.setState({currentPage: event.page, rows: event.rows});
  };

  private onSelectionChange = (event: DataTableSelectionChangeParams): void => {
    const pageSelection: CustomListModel[] = Array.isArray(event.value)
      ? (event.value as CustomListModel[])
      : [];
    const currentPageIds: Set<string> = new Set(this.getCurrentPageLists().map((list) => list.id));
    const selectionsOutsidePage: CustomListModel[] = this.props.selectedLists.filter(
      (list) => !currentPageIds.has(list.id),
    );
    this.props.onSelectionChange([...selectionsOutsidePage, ...pageSelection]);
  };

  private getCurrentPageLists = (): CustomListModel[] => {
    const {lists} = this.props;
    const {rows, currentPage} = this.state;
    const startIndex: number = (currentPage - 1) * rows;
    const endIndex: number = startIndex + rows;
    return lists.slice(startIndex, endIndex);
  };

  private getCurrentPageSelection = (currentData: CustomListModel[]): CustomListModel[] => {
    const currentPageIds: Set<string> = new Set(currentData.map((list) => list.id));
    return this.props.selectedLists.filter((list) => currentPageIds.has(list.id));
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({id: 'pages.customList.table.listName', defaultMessage: 'List Name'}),
        className: 'w-1/4',
        body: (record: CustomListModel) => (
          <div className={'flex items-center text-xs 2xl:text-md'}>
            <CustomTableContentWrapper clipSize={30} text={record.name}/>
          </div>
        ),
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({id: 'pages.customList.table.createdAt', defaultMessage: 'Created At'}),
        className: 'w-1/5',
        body: (record: CustomListModel) => (
          <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
            <CustomTableContentWrapper
              text={record.createdAt ? moment(record.createdAt).format('DD.MM.YYYY HH:mm') : ''}
            />
          </div>
        ),
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({id: 'pages.customList.table.updatedAt', defaultMessage: 'Updated At'}),
        className: 'w-1/5',
        body: (record: CustomListModel) => (
          <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
            <CustomTableContentWrapper
              text={record.updatedAt ? moment(record.updatedAt).format('DD.MM.YYYY HH:mm') : ''}
            />
          </div>
        ),
      },
      {
        field: 'description',
        header: LanguageUtil.get({id: 'pages.customList.table.description', defaultMessage: 'Description'}),
        className: 'w-1/4',
        body: (record: CustomListModel) => (
          <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
            <CustomTableContentWrapper clipSize={40} text={record.description || ''}/>
          </div>
        ),
      },
    ];

    if (RoleHelper.canEdit(ComponentName.CUSTOM_LIST)) {
      columns.push({
        field: 'action',
        header: LanguageUtil.get({id: 'pages.customList.table.action', defaultMessage: 'Action'}),
        className: 'max-w-[80px]',
        sortable: false,
        body: this.getActions,
      });
    }

    return columns;
  };

  private getActions = (record: CustomListModel): ReactNode => {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.props.onDelete(record),
            message: LanguageUtil.get({
              id: 'messages.confirm.remove1',
              defaultMessage: 'Are you sure you want to remove?',
            }),
          });
        }}
      >
        <Icon icon={'delete'} size={20} className={'hover:text-primary text-[#607080] hover:cursor-pointer'}/>
      </div>
    );
  };
}

export default CustomListTableComponent;
